import { useForkedRef } from '@evertel/hooks'
import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react'

type GradientColor = [number, string]

/**
 * Waveform
 *
 * #### Options
 *
 * |      Parameter     |      Description    |  Permitted Values  |       Default      |
 * | ------------------ | ------------------- | ------------------ | ------------------ |
 * | `barWidth`  | Width of the peaks | Number | `undefined` |
 * | `color`  | The main waveform's color | String (hex value) | `undefined` |
 * | `duration` | Audio length in seconds | Number |  `undefined` |
 * | `gradientColors`  | The main waveform's color as an array of arrays containing start positions (from 0 to 1) and colors; example: `[[0, "#888888"], [1, "#aaaaaa"]]` | Array | `undefined` |
 * | `height` | The waveform's height | Integer | `undefined` |
 * | `onClick` | Handler for seeking. This is a function with a single argument of seconds as a number. | function | `() => {}` |
 * | `peaks` | Array of peaks. | Array | `undefined` |
 * | `pixelRatio` | Determines how many pixels to fit inside a single pixel space. | Number | The current device's pixel ratio |
 * | `pos` | The audio's current position in seconds | Number | `undefined` |
 * | `progressColor`  | The progress waveform's color | String (hex value) | `undefined` |
 * | `progressGradientColors`  | The progress waveform's color as an array of arrays containing start positions (from 0 to 1) and colors; example: `[[0, "#888888"], [1, "#aaaaaa"]]` | Array | `undefined` |
 * | `transitionDuration` | Length in milliseconds of the waveform animation transition | Number | `200` |
 */
export interface WaveformProps {
    /** Array/typed array of peak samples (already extracted upstream). */
    peaks: number[] | Float32Array
    /** Total audio duration in seconds. */
    duration: number
    /** Current playback position in seconds. */
    pos: number
    /** Canvas display height in CSS px. */
    height?: number
    /** Base waveform color (used when no gradient). */
    color?: string
    /** Overlay (progress) waveform color (used when no gradient). */
    progressColor?: string
    /** Bar mode: if provided, draws discrete bars of this width (in CSS px). */
    barWidth?: number
    /** Gradient stops for base waveform: [[stop(0-1), color], ...]. */
    gradientColors?: GradientColor[]
    /** Gradient stops for progress overlay: [[stop(0-1), color], ...]. */
    progressGradientColors?: GradientColor[]
    /** Device pixel ratio override; defaults to window.devicePixelRatio. */
    pixelRatio?: number
    /** Click handler: receives seek target in whole seconds. */
    onClick?: (positionInSeconds: number) => void
    /** CSS transition ms used for progress overlay width changes. */
    transitionDuration?: number
    /** Optional className passthrough. */
    className?: string
    /** Optional inline style passthrough. */
    style?: React.CSSProperties
    /** Optional bar showing position. */
    showPositionBar?: boolean
    /** Optional inline position bar style passthrough. */
    positionBarStyle?: React.CSSProperties
}

interface DrawOptions {
    peaks: number[] | Float32Array
    width: number
    height: number
    color: string
    gradientColors?: GradientColor[]
    barWidth?: number
    pixelRatio: number
    normalize: boolean
}

function absMax(values: number[]): number {
    let max = 0
    for (let i = 0; i < values.length; i++) {
        const abs = Math.abs(values[i])
        if (abs > max) max = abs
    }
    return max || 1
}

function max(values: number[]): number {
    let maxVal = -Infinity
    for (let i = 0; i < values.length; i++) {
        if (values[i] > maxVal) maxVal = values[i]
    }
    return maxVal
}

function min(values: number[]): number {
    let minVal = Infinity
    for (let i = 0; i < values.length; i++) {
        if (values[i] < minVal) minVal = values[i]
    }
    return minVal
}

function prepareBarPeaks(peaks: number[] | Float32Array): number[] {
    const peaksArray = Array.isArray(peaks) ? peaks : Array.from(peaks)
    const hasMinVals = peaksArray.some(val => val < 0)
    if (!hasMinVals) return peaksArray

    // Bar mode draws bottom as reflection of top, so strip negative values.
    // If first value is negative, even/odd indexing shifts by one.
    const indexOffset = peaksArray[0] < 0 ? 1 : 0
    return peaksArray.filter((_, index) => (index + indexOffset) % 2 === 0)
}

function prepareWavePeaks(peaks: number[] | Float32Array): number[] {
    const peaksArray = Array.isArray(peaks) ? peaks : Array.from(peaks)
    const hasMinValues = peaksArray.some(val => val < 0)
    if (hasMinValues) return peaksArray

    // When only positive peaks are provided, reflect to create a full hull.
    const reflected: number[] = []
    for (let i = 0; i < peaksArray.length; i++) {
        reflected[2 * i] = peaksArray[i]
        reflected[2 * i + 1] = -peaksArray[i]
    }
    return reflected
}

/**
 * Build a horizontal linear gradient, or return null to signal "use solid color".
 */
function setupGradient(
    ctx: CanvasRenderingContext2D,
    width: number,
    gradientColors?: GradientColor[]
): CanvasGradient | null {
    if (!gradientColors || gradientColors.length === 0) return null

    const gradient = ctx.createLinearGradient(0, 0, width, 0)
    gradientColors.forEach(([stop, color]) => {
        gradient.addColorStop(stop, color)
    })
    return gradient
}

// drawBars: operates purely in CSS px (the canvas context is already scaled).
function drawBars(ctx: CanvasRenderingContext2D, options: DrawOptions): void {
    const { peaks, width, height, color, gradientColors, barWidth, pixelRatio, normalize } = options

    const preparedPeaks = prepareBarPeaks(peaks)
    const halfPixel = 0               // subpixel offset for crisp edges
    const halfH = height / 2          // vertical center in CSS px
    const bar = (barWidth || 1)       // CSS px (no DPR multiply)
    const gap = (pixelRatio === 1 ? 2 : 1)
    const step = bar + gap

    let absMaxVal = 1
    if (normalize) absMaxVal = absMax(preparedPeaks)

    const scale = preparedPeaks.length / width

    // Use gradient if provided; otherwise solid color
    ctx.fillStyle = setupGradient(ctx, width, gradientColors) ?? color

    for (let x = 0; x < width; x += step) {
        const peakIndex = Math.min(preparedPeaks.length - 1, Math.floor(x * scale))
        let h = Math.round((preparedPeaks[peakIndex] / absMaxVal) * halfH)
        if (h === 0) h = 1
        ctx.fillRect(x + halfPixel, halfH - h, bar + halfPixel, h * 2)
    }
}


// drawWaves: also in CSS px; the context scaling handles DPR.
function drawWaves(ctx: CanvasRenderingContext2D, options: DrawOptions): void {
    const { peaks, width, height, color, gradientColors, pixelRatio, normalize } = options

    const preparedPeaks = prepareWavePeaks(peaks)

    // Half-pixel offset for crisp lines
    const halfPixel = 0.5
    const halfH = height / 2
    const length = Math.max(1, Math.floor(preparedPeaks.length / 2))
    const scale = width / length

    let absMaxVal = 1
    if (normalize) {
        const maxVal = max(preparedPeaks)
        const minVal = min(preparedPeaks)
        absMaxVal = -minVal > maxVal ? -minVal : maxVal
    }

    ctx.fillStyle = setupGradient(ctx, width, gradientColors) ?? color

    ctx.beginPath()
    ctx.moveTo(halfPixel, halfH)

    // Draw top edge
    for (let i = 0; i < length; i++) {
        const h = Math.round((preparedPeaks[2 * i] / absMaxVal) * halfH)
        ctx.lineTo(i * scale + halfPixel, halfH - h)
    }

    // Draw bottom edge (reverse)
    for (let i = length - 1; i >= 0; i--) {
        const h = Math.round((preparedPeaks[2 * i + 1] / absMaxVal) * halfH)
        ctx.lineTo(i * scale + halfPixel, halfH - h)
    }

    ctx.fill()

    // Draw median line
    ctx.fillRect(0, halfH - halfPixel, width, halfPixel)
}


const Waveform = React.forwardRef<HTMLDivElement, WaveformProps>(({
    peaks,
    duration,
    pos,
    height = 30,
    color = '#ccc',
    progressColor = '#555555',
    barWidth,
    gradientColors,
    progressGradientColors,
    pixelRatio = typeof window !== 'undefined'
        ? (window.devicePixelRatio || 1)
        : 1,
    onClick = () => { /** no-op */ },
    transitionDuration = 200,
    className,
    style,
    showPositionBar = false,
    positionBarStyle
}, forwardedRef) => {

    // Refs
    const wrapperRef = useRef<HTMLDivElement>(null)
    const mainCanvasRef = useRef<HTMLCanvasElement>(null)
    const progressCanvasRef = useRef<HTMLCanvasElement>(null)

    const mergedRef = useForkedRef(forwardedRef, wrapperRef)

    const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

    useEffect(() => {
        if (!wrapperRef.current) return undefined

        // Initial measurement
        const initialWidth = wrapperRef.current.clientWidth
        if (initialWidth > 0) {
            setDimensions({
                width: initialWidth,
                height
            })
        }

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0]
            if (!entry) return

            const { width } = entry.contentRect
            if (width > 0) {
                setDimensions({
                    width,
                    height
                })
            }
        })

        resizeObserver.observe(wrapperRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [height])

    // Force immediate render on peaks change
    useEffect(() => {
        if (!wrapperRef.current || !peaks) return

        const width = wrapperRef.current.clientWidth
        if (width > 0) {
            setDimensions(prev => {
                if (prev.width !== width) {
                    return { width, height }
                }
                return prev
            })
        }
    }, [peaks, height, dimensions.width])

    // drawCanvas: sizes the bitmap for DPR, resets transform, clears, then scales,
    // and finally draws using CSS-pixel dimensions throughout.
    const drawCanvas = useCallback(
        (canvas: HTMLCanvasElement | null, canvasColor: string, canvasGradientColors?: GradientColor[]) => {
            if (!canvas || !peaks || dimensions.width === 0) return

            const ctx = canvas.getContext('2d')
            if (!ctx) return

            // 1) Bitmap size in *device* pixels for crisp rendering
            const canvasWidth = Math.max(1, Math.floor(dimensions.width * pixelRatio))
            const canvasHeight = Math.max(1, Math.floor(dimensions.height * pixelRatio))
            canvas.width = canvasWidth
            canvas.height = canvasHeight

            // 2) CSS size in logical pixels for layout
            canvas.style.width = `${dimensions.width}px`
            canvas.style.height = `${dimensions.height}px`

            // 3) Reset any prior scaling, clear the *device* pixel bitmap
            ctx.setTransform(1, 0, 0, 1, 0, 0)
            ctx.clearRect(0, 0, canvasWidth, canvasHeight)

            // 4) Map 1 CSS unit -> DPR device pixels
            ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)

            // 5) Draw in CSS units (width/height below are CSS px)
            const drawOptions: DrawOptions = {
                peaks,
                width: dimensions.width,
                height: dimensions.height,
                color: canvasColor,
                gradientColors: canvasGradientColors,
                barWidth,
                pixelRatio, // kept in signature if you need subpixel offsets elsewhere
                normalize: true
            }

            if (barWidth) {
                drawBars(ctx, drawOptions)
            } else {
                drawWaves(ctx, drawOptions)
            }
        },
        [peaks, dimensions, pixelRatio, barWidth]
    )

    // Draw both canvases when dependencies change
    useLayoutEffect(() => {
        drawCanvas(mainCanvasRef.current, color, gradientColors)
        drawCanvas(progressCanvasRef.current, progressColor, progressGradientColors)
    }, [drawCanvas, color, gradientColors, progressColor, progressGradientColors])

    // Click-to-seek (round to whole second)
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (dimensions.width === 0) return

            const rect = e.currentTarget.getBoundingClientRect()
            const offsetX = e.clientX - rect.left
            const percentageOffsetX = offsetX / dimensions.width
            const positionInSeconds = Math.round(percentageOffsetX * duration)

            if (onClick) onClick(positionInSeconds)
        },
        [dimensions.width, duration, onClick]
    )

    // Clamp progress to [0, 1] to avoid over/underflow when pos is out of bounds.
    const ratio = duration > 0 ? Math.min(1, Math.max(0, pos / duration)) : 0
    const progressWidth = dimensions.width * ratio

    // Don't render if no peaks
    if (!peaks || peaks.length === 0) {
        return null
    }

    return (
        <div
            ref={mergedRef}
            onClick={handleClick}
            className={className}
            style={{
                position: 'relative',
                width: '100%',
                height: `100%`,
                cursor: 'pointer',
                ...style
            }}
        >
            {/* Main waveform canvas */}
            <canvas
                ref={mainCanvasRef}
                style={{
                    display: 'block',
                    width: '100%',
                    height: '100%'
                }}
            />

            {/* Progress overlay */}
            <div
                style={{
                    position: 'absolute',
                    zIndex: 2,
                    left: 0,
                    top: 0,
                    overflow: 'hidden',
                    height: '100%',
                    width: `${progressWidth}px`,
                    transition: `width ${transitionDuration}ms ease-in-out`,
                    boxSizing: 'border-box',
                    pointerEvents: 'none'
                }}
            >
                <canvas
                    ref={progressCanvasRef}
                    style={{
                        display: 'block',
                        width: `${dimensions.width}px`,
                        height: '100%'
                    }}
                />
            </div>

            {/* + Optional position bar overlay */}
            {showPositionBar && (
                <div
                    style={{
                        position: 'absolute',
                        opacity: (ratio ? 1 : 0.5),
                        zIndex: 3,
                        top: '50%',
                        left: `${ratio * 100}%`,
                        width: Math.min(barWidth, 3),
                        height: '80%',
                        transform: 'translateY(-50%)',
                        background: '#fff',
                        pointerEvents: 'none',
                        transition: `left ${transitionDuration}ms ease-in-out`,
                        // allow caller to override anything
                        ...positionBarStyle
                    }}
                />
            )}

        </div>
    )
})

Waveform.displayName = 'Waveform'

const areEqual = (a: WaveformProps, b: WaveformProps) =>
    a.pos === b.pos &&
    a.duration === b.duration &&
    a.height === b.height &&
    a.color === b.color &&
    a.progressColor === b.progressColor &&
    a.barWidth === b.barWidth &&
    a.peaks === b.peaks &&
    a.gradientColors === b.gradientColors &&
    a.progressGradientColors === b.progressGradientColors &&
    a.pixelRatio === b.pixelRatio &&
    a.transitionDuration === b.transitionDuration &&
    a.className === b.className &&
    a.style === b.style


// Memoize component with custom comparison. Keep re-renders down.
const WaveformExport = React.memo(Waveform, areEqual)

export { WaveformExport }
